import {
  D3CircleData,
  D3LineData,
  D3RectData,
  d3renderCircle,
  d3renderGradient,
  d3renderLine,
  d3renderPath,
  d3renderPathFilled,
  d3renderRect,
  d3renderText,
} from '../chart.d3';
import { ChartManager, DataRowExt } from '../chart.manager';

import { cloneObject, roundTo } from '@23degrees/shared-code/utilities';

import { anchorToFlexClass, DataImage } from '@23degrees/shared-code/domain/content/entities/components/chart/DataImage';
import { DataLabel } from '@23degrees/shared-code/domain/content/entities/components/chart/DataLabel';

import { getOldFields } from '@23degrees/shared-code/config/dimension.config';
import chroma from 'chroma-js';
import { select } from 'd3-selection';
import { line } from 'd3-shape';
import { overlayImage, overlayText } from '../chart.overlay';
import { color2css, fixLineHeight, strokeDasharrayFormat, tailWindEqual } from '../utilities/utils';
import { CustomBar } from '@23degrees/shared-code/models';
import { unique } from 'std-kit';

// #region renderBarVertical
export const renderBarVertical = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, valueField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, values, bars, waterfall } = typeSpecific.chart;
  const { customBars } = typeSpecific.chart.bars;
  const { labelAxis } = typeSpecific.chart.axis;
  const { backgroundColor } = typeSpecific.style.background;
  const { colors2 } = typeSpecific.style;

  const mapField = chartManager.shouldAggregate(type) ? aggregationField : labelField;
  const { dataType } = typeSpecific.fields[mapField];
  const isLabelDate = dataType === 'date';

  const dynamicStyle = cloneObject(values.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(values.style);

  const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

  const dynamicValues = cloneObject(values);
  dynamicValues.style = dynamicStyle;
  const dataLabel = DataLabel.create(dynamicValues);
  const dataImage = DataImage.create(dynamicValues);

  const data: D3RectData[] = [];
  const lineData: D3LineData[] = []; // Waterfall connecting lines
  const imageData = [];
  const textData = [];
  const overlayData = [];
  let waterfallAcc = 0;
  const waterfallSums = waterfall.sumBars || [];

  for (const [idx, dataRow] of chartManager.data.entries()) {
    const label = dataRow[labelField] as string;
    const groupKey = dataRow[aggregationField] as string;
    const value = dataRow[valueField] as number;

    if (value === undefined) continue;

    let color = chartManager.colorScale(dataRow);
    const isGroupColor = chartManager.shouldAggregate(type) && ['continuous', 'gradient', 'range'].includes(colors2.type);

    if (isGroupColor) {
      const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === label);
      color = chartManager.groupedColorScale(group['~meta']);
    }

    const hoverColor = color2css(chroma(color).darken());

    const rangeAdjust = chartManager.rangeMin >= 0 ? Math.min(chartManager.rangeMin, value) : Math.min(0, value);
    const x = chartManager.shouldAggregate(type) ? chartManager.xScale(groupKey) + chartManager.groupScales[groupKey](label) : chartManager.xScale(label);
    let y = chartManager.yScale(rangeAdjust);
    const width = chartManager.verticalWidth(type, groupKey);
    const height = chartManager.verticalHeight(value > 0 ? value - rangeAdjust : value);

    if (waterfall.enabled) {
      // NOTE: renderBarVertical
      if (chartManager.data[idx - 1]) {
        const prevValue = chartManager.data[idx - 1][valueField] as number;
        const prevLabel = chartManager.data[idx - 1][labelField] as string;
        waterfallAcc += waterfallSums.includes(prevLabel) ? 0 : prevValue;
        const valueOffset = waterfallSums.includes(label) ? (value > 0 ? -value : 0) : value < 0 ? value : 0;
        y = chartManager.yScale(waterfallAcc + valueOffset);
      }
    }

    const click = (event) => {
      chartManager.openPopup(event, dataRow);
      chartManager.mouseClick(event, dataRow);
    };

    const enter = (event, _, node) => {
      select(node).attr('fill', hoverColor);
      chartManager.mouseEnter(event, dataRow);
    };

    const leave = (event, _, node) => {
      select(node).attr('fill', color);
      chartManager.mouseLeave(event, dataRow);
    };

    const move = (event, _, node) => chartManager.mouseMove(event, dataRow);

    const callbacks = { click, enter, leave, move };

    const animate = {
      enabled: animation.enabled,
      delay: animation.delay,
      duration: animation.duration,
      x: roundTo(x),
      y: roundTo(y - height),
      width: roundTo(width),
      height: roundTo(height),
      color: color2css(color),
    };

    // actual svg bar
    data.push({
      x: roundTo(x),
      y: roundTo(chartManager.yScale(0)),
      width: roundTo(width),
      height: 0,
      color: color2css(color),
      animate,
      callbacks,
    });

    if (waterfall.enabled && waterfall.connectLine.enabled) {
      const { stroke, strokeWidth, strokeDasharray } = waterfall.connectLine;
      const lineFn = line()
        // .curve(curve)
        .x((lineObj) => roundTo(lineObj[0])) //  + offset[0]
        .y((lineObj) => roundTo(lineObj[1])); //  + offset[1]

      if (chartManager.data[idx + 1]) {
        const nextLabel = chartManager.data[idx + 1][labelField];
        const nextX = chartManager.xScale(nextLabel); // TODO: StackedCharts
        const yF = value >= 0 ? roundTo(y - height) : roundTo(y);
        const boxLine = lineFn([
          [x + width, yF],
          [nextX, yF],
        ]);

        lineData.push({
          x1: x + width,
          x2: nextX,
          y1: yF,
          y2: yF,
          color: stroke,
          width: strokeWidth,
          lineDash: strokeDasharrayFormat(strokeDasharray),
        });
        /*
        lineData.push({
          color: stroke,
          path: boxLine,
          width: strokeWidth,
          animate: {
            enabled: animation.enabled,
            delay: animation.delay,
            duration: animation.duration,
            // path,
          },
        });
        */
      }
    }

    if (!values.enabled) continue;

    const { dataLabelStr, dataLabelWidth, dataLabelHeight } = dataRow['~meta'];
    const actualSize = undefined; // chartManager.getElementRect(dataLabelStr, chartManager.textRotate ? height : width);

    const availableHeight = chartManager.verticalSpace(value);

    if (customBars) {
      let customBar: CustomBar;
      if (type.includes('grouped')) {
        customBar = customBars.find((item) => {
          const row = chartManager.data[idx];
          const key = `${row[aggregationField]}-${row[labelField]}`;
          return key === item.id;
        });
      } else {
        customBar = customBars.find((item) => chartManager.data[idx][labelField] === item.id);
      }

      if (customBar?.image.enabled) {
        const img = document.createElement('img');
        img.setAttribute('src', `${chartManager.globalService.escapeMediaLink(customBar.image.url)}`);
        img.classList.add('object-contain');

        // img.style.borderRadius = `${customBar.image.style.radius}%`;
        // const { strokeWidth, stroke, enabled } = customBar.image.style.border;
        // if (enabled) img.style.border = `${strokeWidth}px ${stroke} solid`;

        const imgContainer = document.createElement('div');
        const flexClass = anchorToFlexClass(customBar.image.position, 'vertical');
        imgContainer.classList.add('w-full', 'h-full', 'flex', 'justify-center', 'overflow-hidden', flexClass);
        // imgContainer.style.padding = `${customBar.image.style.padding}px`;
        imgContainer.appendChild(img);

        switch (values.position) {
          case 'inside-away-alt-away':
          case 'inside-away-alt-zero':
            imgContainer.style.marginTop = `${dataLabelHeight}px`;
            imgContainer.style.height = `${height - dataLabelHeight - customBar.image.style.padding * 2}px`;
            // Top padding
            break;
          case 'inside-zero-alt-away':
          case 'inside-zero-alt-zero':
            imgContainer.style.marginBottom = `${dataLabelHeight}px`;
            imgContainer.style.height = `${height - dataLabelHeight - customBar.image.style.padding * 2}px`;
            // Bottom padding
            break;
          default:
            break;
        }

        const renderImgConfig = dataImage.getRenderConfig({
          mode: 'vertical',
          animation,
          x: roundTo(x),
          y: roundTo(y - height),
          width: roundTo(width),
          height: roundTo(height),
          value,
          imgHeight: 0,
          imgWidth: 0,
          imgRotate: false,
          position: customBar.image.position,
          htmlStr: imgContainer.outerHTML,
          callbacks: {},
        });

        if (customBar.image.url && renderImgConfig) imageData.push(renderImgConfig);
      }
    }

    // callbacks
    const clickQ = (event, _, node) => {};
    const enterQ = (event, _, node) => {};
    const leaveQ = (event, _, node) => {};
    const moveQ = (event, _, node) => {};

    const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

    const waterfallAccScaledOffset = waterfallSums.includes(label) ? (value > 0 ? 0 : value) : value;

    const renderConfig = dataLabel.getRenderConfig({
      mode: 'vertical',
      animation,
      actualSize,
      htmlStr: dataLabelStr,
      value,
      availableWidth: 0,
      availableHeight,
      textRotate: chartManager.textRotate,
      color,
      backgroundColor,
      x,
      y,
      width,
      height,
      labelWidth: dataLabelWidth,
      labelHeight: dataLabelHeight,
      chartWidth: chartManager.chartWidth,
      chartHeight: chartManager.chartHeight,
      zeroX: chartManager.xScale(0),
      zeroY: chartManager.yScale(0),
      dataLabelOffset: chartManager.dataLabelOffset,
      callbacks: callbacksQ,
      valuePosition: values.position,
      positionAltInside: values.positionAltInside,
      positionAltOutside: values.positionAltOutside,
      waterfall: waterfall.enabled,
      waterfallAcc: waterfallAcc,
      waterfallAccScaled: chartManager.verticalHeight(waterfallAcc + waterfallAccScaledOffset),
    });

    if (renderConfig) overlayData.push(renderConfig);
  }

  // clear
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderRect(chartManager.chart.element, data, '.chart', chartManager.animationStop);

  d3renderLine(chartManager.chart.element, waterfall.enabled && waterfall.connectLine.enabled ? lineData : [], '.chart');
  overlayImage(chartManager.overlay, customBars ? imageData : [], '.overlay-images');

  overlayText(chartManager.overlay, overlayData, '.overlay-values');
};
// #endregion

// #region renderBarHorizontal
export const renderBarHorizontal = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, valueField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, values, dynamicHeight, dynamicLabelHeight, bars, waterfall } = typeSpecific.chart;
  const { paddingOuterPx, paddingInnerPx, customBars, groupPaddingInnerPx, groupPaddingOuterPx } = typeSpecific.chart.bars;
  const { labelAxis } = typeSpecific.chart.axis;
  const { backgroundColor } = typeSpecific.style.background;

  const dynamicStyle = cloneObject(values.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(values.style);

  const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
  const valueMarginTop = chartManager.getStyleInPx(dynamicValueStyle.margin, 'top');
  const valueMarginBottom = chartManager.getStyleInPx(dynamicValueStyle.margin, 'bottom');
  const valueMarginLeft = chartManager.getStyleInPx(dynamicValueStyle.margin, 'left');
  const valueMarginRight = chartManager.getStyleInPx(dynamicValueStyle.margin, 'right');
  // const valueHeight = oneLineHeight + valueMarginTop + valueMarginBottom;

  const dynamicValues = cloneObject(values);
  dynamicValues.style = dynamicStyle;
  const dataLabel = DataLabel.create(dynamicValues);
  const dataImage = DataImage.create(dynamicValues);

  const data: D3RectData[] = [];
  const lineData: D3LineData[] = []; // Waterfall connecting lines
  const imageData = []; // Custom Bar Image
  const textData = [];
  const overlayData = [];
  let waterfallAcc = 0;
  const waterfallSums = waterfall.sumBars || [];

  for (const [index, dataRow] of chartManager.data.entries()) {
    const label = dataRow[labelField] as string;
    const groupKey = dataRow[aggregationField] as string;
    const value = dataRow[valueField] as number;

    if (value === undefined) continue;

    const color = chartManager.colorScale(dataRow);
    const hoverColor = color2css(chroma(color).darken());

    const rangeAdjust = chartManager.rangeMin >= 0 ? Math.min(chartManager.rangeMin, value) : Math.min(0, value);
    let x = chartManager.xScale(rangeAdjust);
    let width = chartManager.horizontalWidth(value > 0 ? value - rangeAdjust : value);

    let y, height;
    // yScaleOrdinal
    if (chartManager.shouldAggregate(type)) {
      let { item } = chartManager.yScaleOrdinal(groupKey);
      const offsetY = chartManager.groupScalesOrdinals[groupKey](label);
      y = item.y + offsetY;
      height = item.height;
    } else {
      let { item } = chartManager.yScaleOrdinal(label);
      y = item.y;
      height = item.height;
    }

    if (waterfall.enabled) {
      // NOTE: renderBarHorizontal
      if (chartManager.data[index - 1]) {
        const prevValue = chartManager.data[index - 1][valueField] as number;
        const prevLabel = chartManager.data[index - 1][labelField] as string;
        waterfallAcc += waterfallSums.includes(prevLabel) ? 0 : prevValue;
        const valueOffset = waterfallSums.includes(label) ? (value > 0 ? -value : 0) : value < 0 ? value : 0;
        x = chartManager.xScale(waterfallAcc + valueOffset);
      }
    }

    const click = (event) => {
      chartManager.openPopup(event, dataRow);
      chartManager.mouseClick(event, dataRow);
    };

    const enter = (event, _, node) => {
      select(node).attr('fill', hoverColor);
      chartManager.mouseEnter(event, dataRow);
    };

    const leave = (event, _, node) => {
      select(node).attr('fill', color);
      chartManager.mouseLeave(event, dataRow);
    };

    const move = (event, _, node) => chartManager.mouseMove(event, dataRow);

    const callbacks = { click, enter, leave, move };

    const animate = {
      enabled: animation.enabled,
      delay: animation.delay,
      duration: animation.duration,
      x: roundTo(x),
      y: roundTo(y),
      width: roundTo(width),
      height: roundTo(height),
      color: color2css(color),
    };

    data.push({
      x: roundTo(chartManager.xScale(0)),
      y: roundTo(y),
      width: 0,
      height: roundTo(height),
      color: color2css(color),
      animate,
      callbacks,
    });

    if (waterfall.enabled && waterfall.connectLine.enabled) {
      const { stroke, strokeWidth, strokeDasharray } = waterfall.connectLine;
      const lineFn = line()
        // .curve(curve)
        .x((lineObj) => roundTo(lineObj[0])) //  + offset[0]
        .y((lineObj) => roundTo(lineObj[1])); //  + offset[1]

      if (chartManager.data[index + 1]) {
        const nextLabel = chartManager.data[index + 1][labelField];
        const { item } = chartManager.yScaleOrdinal(nextLabel);
        const xF = value >= 0 ? roundTo(x + width) : roundTo(x);
        const yF = item.y;

        const boxLine = lineFn([
          [xF, y + height],
          [xF, yF],
        ]);

        lineData.push({
          x1: xF,
          x2: xF,
          y1: y + height,
          y2: yF,
          color: stroke,
          width: strokeWidth,
          lineDash: strokeDasharrayFormat(strokeDasharray),
        });
        /*
        lineData.push({
          color: stroke,
          path: boxLine,
          width: strokeWidth,
          animate: {
            enabled: animation.enabled,
            delay: animation.delay,
            duration: animation.duration,
            // path,
          },
        });
        */
      }
    }

    if (!values.enabled) continue;

    const { dataLabelStr, dataLabelWidth, dataLabelHeight } = dataRow['~meta'];
    const actualSize = undefined; // chartManager.getElementRect(dataLabelStr, chartManager.textRotate ? height : width);

    const availableWidth = chartManager.horizontalSpace(value);

    if (customBars) {
      let customBar: CustomBar;
      if (type.includes('grouped')) {
        customBar = customBars.find((item) => {
          const row = chartManager.data[index];
          const key = `${row[aggregationField]}-${row[labelField]}`;
          return key === item.id;
        });
      } else {
        customBar = customBars.find((item) => chartManager.data[index][labelField] === item.id);
      }

      if (customBar?.image.enabled) {
        const img = document.createElement('img');
        img.setAttribute('src', `${chartManager.globalService.escapeMediaLink(customBar.image.url)}`);
        img.classList.add('object-contain', 'h-full');

        // img.style.borderRadius = `${customBar.image.style.radius}%`;
        // const { strokeWidth, stroke, enabled } = customBar.image.style.border;
        // if (enabled) img.style.border = `${strokeWidth}px ${stroke} solid`;

        const imgContainer = document.createElement('div');
        const flexClass = anchorToFlexClass(customBar.image.position, 'horizontal');
        imgContainer.classList.add('w-full', 'h-full', 'flex', 'items-center', 'overflow-hidden', flexClass);
        // imgContainer.style.padding = `${customBar.image.style.padding}px`;
        imgContainer.appendChild(img);

        switch (values.position) {
          case 'inside-away-alt-away':
          case 'inside-away-alt-zero':
            imgContainer.style.marginRight = `${dataLabelWidth}px`;
            // Right padding
            break;
          case 'inside-zero-alt-away':
          case 'inside-zero-alt-zero':
            imgContainer.style.marginLeft = `${dataLabelWidth}px`;
            // Left padding
            break;
          default:
            break;
        }

        const renderImgConfig = dataImage.getRenderConfig({
          mode: 'horizontal',
          animation,
          x: roundTo(x),
          y: roundTo(y),
          width: roundTo(width),
          height: roundTo(height),
          value,
          imgHeight: 0,
          imgWidth: 0,
          imgRotate: false,
          position: customBar.image.position,
          htmlStr: imgContainer.outerHTML,
          callbacks: {},
        });
        if (customBar.image.url && renderImgConfig) imageData.push(renderImgConfig);
      }
    }

    // callbacks
    const clickQ = (event, _, node) => {};
    const enterQ = (event, _, node) => {};
    const leaveQ = (event, _, node) => {};
    const moveQ = (event, _, node) => {};

    const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

    const waterfallAccScaledOffset = waterfallSums.includes(label) ? (value > 0 ? 0 : value) : value;

    const renderConfig = dataLabel.getRenderConfig({
      mode: 'horizontal',
      animation,
      actualSize,
      htmlStr: dataLabelStr,
      value,
      availableWidth,
      availableHeight: 0,
      textRotate: chartManager.textRotate,
      color,
      backgroundColor,
      x,
      y,
      width,
      height,
      labelWidth: dataLabelWidth,
      labelHeight: dataLabelHeight,
      chartWidth: chartManager.chartWidth,
      chartHeight: chartManager.chartHeight,
      zeroX: chartManager.xScale(0),
      zeroY: chartManager.yScale(0),
      dataLabelOffset: chartManager.dataLabelOffset,
      callbacks: callbacksQ,
      valuePosition: values.position,
      positionAltInside: values.positionAltInside,
      positionAltOutside: values.positionAltOutside,
      waterfall: waterfall.enabled,
      waterfallAcc: waterfallAcc,
    });

    if (renderConfig) overlayData.push(renderConfig);
  }

  // clear
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderRect(chartManager.chart.element, data, '.chart', chartManager.animationStop);
  // d3renderText(chartManager.chart, textData, '.text');

  d3renderLine(chartManager.chart.element, waterfall.enabled && waterfall.connectLine.enabled ? lineData : [], '.chart');
  overlayImage(chartManager.overlay, customBars ? imageData : [], '.overlay-images');

  overlayText(chartManager.overlay, overlayData, '.overlay-values');
};
// #endregion

export const renderBarVerticalGrouped = (chartManager: ChartManager): void => {
  renderBarVertical(chartManager);
};

export const renderBarHorizontalGrouped = (chartManager: ChartManager): void => {
  renderBarHorizontal(chartManager);
};

// #region renderBarVerticalStacked
export const renderBarVerticalStacked = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, valueField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, values, bars, waterfall } = typeSpecific.chart;
  const { customBars } = typeSpecific.chart.bars;
  const { backgroundColor } = typeSpecific.style.background;
  const { colors2 } = typeSpecific.style;

  const dynamicStyle = cloneObject(values.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(values.style);

  const isGroupColor = ['continuous', 'gradient', 'range'].includes(colors2.type);

  const dynamicValues = cloneObject(values);
  dynamicValues.style = dynamicStyle;
  const dataLabel = DataLabel.create(dynamicValues);
  const dataImage = DataImage.create(dynamicValues);

  const data: D3RectData[] = [];
  const dataMap = [];
  const imageData = [];
  const textData = [];
  const overlayData = [];
  const waterfallAcc: Record<string, number> = {};
  const waterfallSums = waterfall.sumBars || [];

  chartManager.dataStackPositive.reverse();
  chartManager.dataStackNegative.reverse();

  const stackOrder = unique(chartManager.data.map((row) => row[aggregationField] as string));

  // In case of waterfall, get accumulative for each category
  if (waterfall.enabled) {
    for (const dataRow of chartManager.data) {
      const group = dataRow[aggregationField] as string;
      const value = dataRow[valueField] as number;

      if (!waterfallAcc[group]) waterfallAcc[group] = 0;
      waterfallAcc[group] += value;
    }

    for (let i = 1; i < stackOrder.length; i++) {
      waterfallAcc[stackOrder[i]] += waterfallSums.includes(stackOrder[i]) ? 0 : waterfallAcc[stackOrder[i - 1]];
    }
  }

  const dataStack = [...chartManager.dataStackNegative, ...chartManager.dataStackPositive.reverse()];
  const imageModes: Record<string, string | null> = {};

  // Get Order Mappings
  const groupMap: Record<string, DataRowExt[]> = {};
  for (const stackObj of dataStack) {
    const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === stackObj.key);
    if (!group) continue;

    for (const pointObj of stackObj) {
      const pointRow = pointObj.data['~meta'].data.find((row) => row[labelField] === stackObj.key);
      if (!pointRow || pointObj[0] - pointObj[1] === 0) continue;
      const pointGroup = pointRow[aggregationField] as string;

      if (!groupMap[pointGroup]) groupMap[pointGroup] = [];
      groupMap[pointGroup].push(pointRow);
    }
  }

  for (const stackObj of dataStack) {
    const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === stackObj.key);
    if (!group) continue;

    for (const pointObj of stackObj) {
      const pointRow = pointObj.data['~meta'].data.find((row) => row[labelField] === stackObj.key);
      if (!pointRow || pointObj[0] - pointObj[1] === 0) continue;

      const pointGroup = pointRow[aggregationField] as string;

      const color = isGroupColor ? chartManager.groupedColorScale(group['~meta']) : chartManager.colorScale(pointRow);
      const hoverColor = color2css(chroma(color).darken());

      const isNegative = pointObj[1] < 0;
      const aggregation = pointObj.data['~meta'].group;

      const x = chartManager.xScale(aggregation);
      let y = chartManager.yScale(0);
      let realY = chartManager.yScale(pointObj[0]);
      let valueY = chartManager.yScale(pointObj[1]);

      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(pointGroup);
        // Omit the first one
        if (idx > 0 && stackOrder[idx - 1]) {
          const offset = waterfallAcc[stackOrder[idx - 1]];
          y = chartManager.yScale(offset);
          realY = chartManager.yScale(pointObj[0] + offset);
          valueY = chartManager.yScale(pointObj[1] + offset);
        }
      }

      const width = chartManager.xScaleBandWidth();
      const height = Math.abs(y - valueY);
      const realHeight = Math.abs(realY - valueY);

      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(pointGroup);
        if (waterfallSums.includes(stackOrder[idx])) {
          valueY = y;
          realY = y + height;
          y = y + realHeight;
        }
      }

      const click = (event) => {
        chartManager.openPopup(event, pointRow);
        chartManager.mouseClick(event, pointRow);
      };

      const enter = (event, _, node) => {
        select(node).attr('fill', hoverColor);
        chartManager.mouseEnter(event, pointRow);
      };

      const leave = (event, _, node) => {
        select(node).attr('fill', color);
        chartManager.mouseLeave(event, pointRow);
      };

      const move = (event, _, node) => chartManager.mouseMove(event, pointRow);

      const callbacks = { click, enter, leave, move };

      let _y = roundTo(isNegative ? realY : realY - realHeight);
      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(pointGroup);
        if (waterfallSums.includes(stackOrder[idx]) && isNegative) {
          _y = y - height - realHeight;
        }
      }

      const animate = {
        enabled: animation.enabled,
        delay: animation.delay,
        duration: animation.duration,
        x: roundTo(x),
        y: _y,
        width: roundTo(width),
        height: roundTo(realHeight),
        color: color2css(color),
      };

      // actual svg bar
      data.push({
        x: roundTo(x),
        y: roundTo(y),
        width: roundTo(width),
        height: 0,
        color: color2css(color),
        animate,
        callbacks,
      });

      dataMap.push({
        x: roundTo(x),
        y: roundTo(isNegative ? realY : realY - realHeight),
        width: roundTo(width),
        height: roundTo(realHeight),
        label: `${pointGroup}-${pointRow[labelField]}`,
        pointRow,
      });

      const value = pointObj[1];
      if (!values.enabled) continue;

      const { dataLabelStr, dataLabelWidth, dataLabelHeight } = pointRow['~meta'];
      const actualSize = undefined; // chartManager.getElementRect(dataLabelStr, chartManager.textRotate ? height : width);

      const availableHeight = chartManager.verticalSpace(value);

      // callbacks
      const clickQ = (event, _, node) => {};
      const enterQ = (event, _, node) => {};
      const leaveQ = (event, _, node) => {};
      const moveQ = (event, _, node) => {};

      const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

      const barIdx = groupMap[pointGroup].findIndex((item) => item['~meta'].id === pointRow['~meta'].id);

      let flex: string | null;
      switch (values.positionAltOutside) {
        case 'join':
          // Check first and last (On vertical is inverted)
          flex = 'center';
          if (barIdx === 0) flex = 'flex-start';
          if (barIdx === groupMap[pointGroup].length - 1) flex = 'flex-end';
          break;
        case 'spread':
          // Check first and last (On vertical is inverted)
          flex = 'center';
          if (barIdx === 0) flex = 'flex-end';
          if (barIdx === groupMap[pointGroup].length - 1) flex = 'flex-start';
          break;
        case 'center':
          flex = 'center';
          break;
        default:
          flex = null;
          break;
      }

      // Save position in case custom bar
      imageModes[pointRow['~meta'].id] = flex;

      let labelY = isNegative ? y + height : y - height + realHeight;
      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(pointGroup);
        if (waterfallSums.includes(stackOrder[idx])) labelY = isNegative ? y - height : y + Math.abs(height - realHeight);
      }

      const renderConfig: any = dataLabel.getRenderConfig({
        mode: 'vertical',
        animation,
        actualSize,
        htmlStr: dataLabelStr,
        value,
        availableWidth: 0,
        availableHeight,
        textRotate: chartManager.textRotate,
        color,
        backgroundColor,
        x,
        y: labelY,
        width,
        height: realHeight,
        labelWidth: dataLabelWidth,
        labelHeight: dataLabelHeight,
        chartWidth: chartManager.chartWidth,
        chartHeight: chartManager.chartHeight,
        zeroX: chartManager.xScale(0),
        zeroY: chartManager.yScale(0),
        dataLabelOffset: chartManager.dataLabelOffset,
        callbacks: callbacksQ,
        valuePosition: 'inside', // values.position,
        positionAltInside: 'stack', // values.positionAltInside
        positionAltOutside: values.positionAltOutside,
        flex,
      });

      if (renderConfig) overlayData.push(renderConfig);
    }
  }

  if (values.enabled && values.sumStacked) {
    const sumLabel = DataLabel.create({
      enabled: true,
      position: 'inside',
      lightnessThreshold: 0.5,
      format: values.format,
      style: values.sumStyle,
      darkColor: values.sumStyle.color,
      lightColor: values.sumStyle.color,
      forceDraw: values.forceDraw,
      quillValue: values.sumQuill,
      valueAlignment: values.valueAlignment,
    });

    const last = chartManager.dataStackPositive.length - 1;
    if (values.sumStackedTotal) {
      const sp = chartManager.dataStackPositive[last];
      const sn = chartManager.dataStackNegative[0];
      // Calculate totals and save them on the index [0]
      for (let i = 0; i < sp.length; i++) chartManager.dataStackPositive[last][i][0] = sn[i][1] + sp[i][1] > 0 ? sn[i][1] + sp[i][1] : 0;
      for (let i = 0; i < sn.length; i++) chartManager.dataStackNegative[0][i][0] = sn[i][1] + sp[i][1] < 0 ? sn[i][1] + sp[i][1] : 0;
      // Nullify values that should not be shown
      for (let i = 0; i < sp.length; i++) if (sn[i][1] + sp[i][1] < 0) chartManager.dataStackPositive[last][i][1] = 0;
      for (let i = 0; i < sn.length; i++) if (sn[i][1] + sp[i][1] > 0) chartManager.dataStackNegative[0][i][1] = 0;
    }

    const dataStackRange = [...chartManager.dataStackNegative[0], ...chartManager.dataStackPositive[last]];

    for (const stackRange of dataStackRange) {
      let stackSum = stackRange[1];
      if (stackSum === 0) continue;

      const sumRow = cloneObject(stackRange.data['~meta'].data[0]);
      sumRow[valueField] = values.sumStackedTotal ? stackRange[0] : stackSum;

      if (waterfall.enabled && sumRow[aggregationField]) sumRow[valueField] = waterfallAcc[sumRow[aggregationField] as string];

      const dynamicDataLabelStyle = cloneObject(values.sumStyle);
      dynamicDataLabelStyle.size = chartManager.getDynamicFontSize(values.sumStyle);

      const dataSumQuill = chartManager.getHTMLandRect(values.sumQuill, dynamicDataLabelStyle, sumRow, chartManager.content, true);
      if (chartManager.textRotate) {
        const { width, height } = chartManager.getElementRect(dataSumQuill.html, undefined, true, undefined, true);
        dataSumQuill.width = width;
        dataSumQuill.height = height;
      }

      const isNegative = stackRange[1] < 0;
      const aggregation = stackRange.data['~meta'].group;

      const x = chartManager.xScale(aggregation);
      let y = chartManager.yScale(0);
      const realY = chartManager.yScale(stackRange[1]);
      const valueY = chartManager.yScale(stackRange[1]) + dataSumQuill.height * (isNegative ? 1 : -1);

      const width = chartManager.xScaleBandWidth();
      const height = Math.abs(y - valueY);
      const realHeight = Math.abs(realY - valueY);

      const availableHeight = chartManager.verticalSpace(stackSum);

      // callbacks
      const clickQ = (event, _, node) => {};
      const enterQ = (event, _, node) => {};
      const leaveQ = (event, _, node) => {};
      const moveQ = (event, _, node) => {};

      const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

      if (waterfall.enabled) {
        const pointGroup = sumRow[aggregationField] as string;
        const idx = stackOrder.indexOf(pointGroup);
        // Omit the first one
        if (idx > 0 && stackOrder[idx - 1]) {
          const offset = waterfallAcc[stackOrder[idx - 1]];
          y = chartManager.yScale(offset);
        }
        if (waterfallSums.includes(stackOrder[idx])) {
          y = isNegative ? y - height + realHeight : y + height - realHeight;
          stackSum += sumRow[valueField] as number;
        }
      }

      const renderConfig = sumLabel.getRenderConfig({
        ignoreColor: true,
        mode: 'vertical',
        animation,
        actualSize: undefined,
        htmlStr: dataSumQuill.html,
        value: stackSum,
        availableWidth: 0,
        availableHeight,
        textRotate: chartManager.textRotate,
        color: backgroundColor,
        backgroundColor,
        x,
        y: isNegative ? y + height : y - height + realHeight,
        width,
        height: realHeight,
        labelWidth: dataSumQuill.width,
        labelHeight: dataSumQuill.height,
        chartWidth: chartManager.chartWidth,
        chartHeight: chartManager.chartHeight,
        zeroX: chartManager.xScale(0),
        zeroY: chartManager.yScale(0),
        dataLabelOffset: chartManager.dataLabelOffset,
        callbacks: callbacksQ,
        valuePosition: 'inside', // values.position,
        positionAltInside: 'stack', // values.positionAltInside
        positionAltOutside: values.positionAltOutside,
      });

      if (renderConfig) overlayData.push(renderConfig);
    }
  }

  const lineData: D3LineData[] = [];
  if (waterfall.enabled && waterfall.connectLine.enabled) {
    const { stroke, strokeWidth, strokeDasharray } = waterfall.connectLine;
    // Get y positions
    const ys = Object.entries(waterfallAcc)
      .map((acc) => acc[1])
      .slice(0, -1);
    // Get x positions
    const barX = [];
    for (const stack of [...chartManager.dataStackPositive, ...chartManager.dataStackNegative]) {
      for (const obj of stack) {
        const aggregation = obj.data['~meta'].group;
        const x = chartManager.xScale(aggregation);
        if (!barX.includes(x)) barX.push(x);
      }
    }

    const width = chartManager.xScaleBandWidth();
    for (const [idx, y] of ys.entries()) {
      const x = barX[idx] + width;
      lineData.push({
        x1: x,
        x2: barX[idx + 1],
        y1: chartManager.yScale(y),
        y2: chartManager.yScale(y),
        color: stroke,
        width: strokeWidth,
        lineDash: strokeDasharrayFormat(strokeDasharray),
      });
    }
  }

  if (customBars) {
    for (const bar of customBars) {
      if (!bar.image.enabled) continue;

      const barAttrs = dataMap.find((item) => bar.id === item.label);
      const labelFlex = imageModes[barAttrs.label];

      const img = document.createElement('img');
      img.setAttribute('src', `${chartManager.globalService.escapeMediaLink(bar.image.url)}`);
      img.classList.add('object-contain');

      // const { strokeWidth, stroke, enabled } = bar.image.style.border;
      // if (enabled) img.style.border = `${strokeWidth}px ${stroke} solid`;

      const imgContainer = document.createElement('div');
      const imgFlexClass = anchorToFlexClass(bar.image.position, 'vertical');
      imgContainer.classList.add('w-full', 'h-full', 'flex', 'justify-center', 'overflow-hidden', imgFlexClass);
      // imgContainer.style.padding = `${bar.image.style.padding}px`;
      imgContainer.appendChild(img);

      // Adapt image to label in case positionMode flow
      if (bar.image.positionMode === 'absolute') {
        const { top, bottom } = bar.image.style.margin || { top: 0, bottom: 0 };
        imgContainer.style.marginTop = `${top}px`;
        imgContainer.style.marginBottom = `${bottom}px`;
      } else {
        const { totalHeight } = barAttrs.actualObj['~meta'];
        if (tailWindEqual[imgFlexClass] === labelFlex) {
          if (labelFlex === 'flex-start') imgContainer.style.marginTop = `${totalHeight}px`;
          if (labelFlex === 'flex-end') imgContainer.style.marginBottom = `${totalHeight}px`;
        }
      }

      const renderImgConfig = dataImage.getRenderConfig({
        mode: 'horizontal',
        animation,
        value: barAttrs.actualObj[valueField],
        x: roundTo(barAttrs.x),
        y: roundTo(barAttrs.y),
        width: roundTo(barAttrs.width),
        height: roundTo(barAttrs.height),
        imgHeight: 0,
        imgWidth: 0,
        imgRotate: false,
        position: bar.image.position,
        htmlStr: imgContainer.outerHTML,
        callbacks: {},
      });

      if (bar.image.url && renderImgConfig) imageData.push(renderImgConfig);
    }
  }

  // clear
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderRect(chartManager.chart.element, data, '.chart', chartManager.animationStop);
  // d3renderText(chartManager.chart, textData, '.text');

  d3renderLine(chartManager.chart.element, waterfall.enabled && waterfall.connectLine.enabled ? lineData : [], '.chart');
  overlayImage(chartManager.overlay, customBars ? imageData : [], '.overlay-images');

  overlayText(chartManager.overlay, overlayData, '.overlay-values');
};
// #endregion

// #region renderBarHorizontalStacked
export const renderBarHorizontalStacked = (chartManager: ChartManager): void => {
  const { type, typeSpecific } = chartManager.content;
  const { labelField, valueField, aggregationField } = getOldFields(chartManager.content.type, chartManager.content?.typeSpecific?.dimensions);
  const { animation, values, dynamicHeight, bars, waterfall } = typeSpecific.chart;
  const { paddingOuterPx, customBars } = typeSpecific.chart.bars;
  const { labelAxis } = typeSpecific.chart.axis;
  const { backgroundColor } = typeSpecific.style.background;
  const { colors2 } = typeSpecific.style;

  const dynamicStyle = cloneObject(values.style);
  dynamicStyle.size = chartManager.getDynamicFontSize(values.style);

  const dynamicValueStyle = cloneObject(labelAxis.valueStyle);
  dynamicValueStyle.size = chartManager.getDynamicFontSize(labelAxis.valueStyle);

  const oneLineHeight = dynamicValueStyle.size + dynamicValueStyle.size * (fixLineHeight(dynamicValueStyle.lineheight.value) - 1);
  const valueMarginTop = chartManager.getStyleInPx(dynamicValueStyle.margin, 'top');
  const valueMarginBottom = chartManager.getStyleInPx(dynamicValueStyle.margin, 'bottom');
  const valueMarginLeft = chartManager.getStyleInPx(dynamicValueStyle.margin, 'left');
  const valueMarginRight = chartManager.getStyleInPx(dynamicValueStyle.margin, 'right');
  const valueHeight = oneLineHeight + valueMarginTop + valueMarginBottom;

  const isGroupColor = ['continuous', 'gradient', 'range'].includes(colors2.type);

  const dataLabel = DataLabel.create(values);
  const dataImage = DataImage.create(values);

  const data: D3RectData[] = [];
  const dataMap = [];
  const imageData = [];
  const textData = [];
  const overlayData = [];
  const waterfallAcc: Record<string, number> = {};
  const waterfallSums = waterfall.sumBars || [];

  chartManager.dataStackPositive.reverse();
  chartManager.dataStackNegative.reverse();

  const stackOrder = unique(chartManager.data.map((row) => row[aggregationField] as string));

  // In case of waterfall, get accumulative for each category
  if (waterfall.enabled) {
    for (const dataRow of chartManager.data) {
      const group = dataRow[aggregationField] as string;
      const value = dataRow[valueField] as number;

      if (!waterfallAcc[group]) waterfallAcc[group] = 0;
      waterfallAcc[group] += value;
    }

    for (let i = 1; i < stackOrder.length; i++) {
      waterfallAcc[stackOrder[i]] += waterfallSums.includes(stackOrder[i]) ? 0 : waterfallAcc[stackOrder[i - 1]];
    }
  }

  const dataStack = [...chartManager.dataStackNegative, ...chartManager.dataStackPositive.reverse()];
  const imageModes: Record<string, string | null> = {};

  // Get Order Mappings
  const groupMap: Record<string, DataRowExt[]> = {};
  for (const stackObj of dataStack) {
    const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === stackObj.key);
    if (!group) continue;

    for (const pointObj of stackObj) {
      const pointRow = pointObj.data['~meta'].data.find((row) => row[labelField] === stackObj.key);
      if (!pointRow || pointObj[0] - pointObj[1] === 0) continue;
      const pointGroup = pointRow[aggregationField] as string;

      if (!groupMap[pointGroup]) groupMap[pointGroup] = [];
      groupMap[pointGroup].push(pointRow);
    }
  }

  for (const stackObj of dataStack) {
    const group = chartManager.dataGroup.find((groupObj) => groupObj[0] === stackObj.key);
    if (!group) continue;

    for (const pointObj of stackObj) {
      const pointRow = pointObj.data['~meta'].data.find((row) => row[labelField] === stackObj.key);
      if (!pointRow || pointObj[0] - pointObj[1] === 0) continue;

      const groupKey = pointRow[aggregationField] as string;

      const color = isGroupColor ? chartManager.groupedColorScale(group['~meta']) : chartManager.colorScale(pointRow);
      const hoverColor = color2css(chroma(color).darken());

      const isNegative = pointObj[1] < 0;
      const aggregation = pointObj.data['~meta'].group;

      let x = chartManager.xScale(0);
      let realX = chartManager.xScale(pointObj[0]);
      let valueX = chartManager.xScale(pointObj[1]);

      // let y = chartManager.yScale(aggregation);
      // let height = chartManager.yScaleBandWidth();

      // yScaleOrdinal
      const { item } = chartManager.yScaleOrdinal(groupKey);
      const y = item.y;
      const height = item.height;

      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(groupKey);
        // Omit the first one
        if (idx > 0 && stackOrder[idx - 1]) {
          const offset = waterfallAcc[stackOrder[idx - 1]];
          x = chartManager.xScale(offset);
          realX = chartManager.xScale(pointObj[0] + offset);
          valueX = chartManager.xScale(pointObj[1] + offset);
        }
      }

      const width = Math.abs(valueX - x);
      const realWidth = Math.abs(valueX - realX);

      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(groupKey);
        if (waterfallSums.includes(stackOrder[idx])) {
          valueX = x;
          realX = x - width;
          x = x - realWidth;
        }
      }

      const click = (event) => {
        chartManager.openPopup(event, pointRow);
        chartManager.mouseClick(event, pointRow);
      };

      const enter = (event, _, node) => {
        select(node).attr('fill', hoverColor);
        chartManager.mouseEnter(event, pointRow);
      };

      const leave = (event, _, node) => {
        select(node).attr('fill', color);
        chartManager.mouseLeave(event, pointRow);
      };

      const move = (event, _, node) => chartManager.mouseMove(event, pointRow);

      const callbacks = { click, enter, leave, move };

      let _x = roundTo(isNegative ? realX - realWidth : realX);
      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(groupKey);
        if (waterfallSums.includes(stackOrder[idx]) && isNegative) {
          _x = Math.abs(x + Math.abs(valueX - realX));
        }
      }

      const animate = {
        enabled: animation.enabled,
        delay: animation.delay,
        duration: animation.duration,
        // x: roundTo(isNegative ? realX - realWidth : realX),
        x: _x,
        y: roundTo(y),
        width: roundTo(realWidth),
        height: roundTo(height),
        color: color2css(color),
      };

      data.push({
        x: roundTo(x),
        y: roundTo(y),
        width: 0,
        height: roundTo(height),
        color: color2css(color),
        animate,
        callbacks,
      });

      dataMap.push({
        x: roundTo(isNegative ? realX - realWidth : realX),
        y: roundTo(y),
        width: roundTo(realWidth),
        height: roundTo(height),
        label: `${groupKey}-${pointRow[labelField]}`,
        pointRow,
      });

      const value = pointObj[1];
      if (!values.enabled) continue;

      const { dataLabelStr, dataLabelWidth, dataLabelHeight } = pointRow['~meta'];
      const actualSize = undefined; // chartManager.getElementRect(dataLabelStr, chartManager.textRotate ? height : width);

      const availableWidth = chartManager.horizontalSpace(value);

      // callbacks
      const clickQ = (event, _, node) => {};
      const enterQ = (event, _, node) => {};
      const leaveQ = (event, _, node) => {};
      const moveQ = (event, _, node) => {};

      const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

      const barIdx = groupMap[groupKey].findIndex((item) => item['~meta'].id === pointRow['~meta'].id);

      let flex: string | null;
      switch (values.positionAltOutside) {
        case 'join':
          // Check first and last
          flex = 'center';
          if (barIdx === 0) flex = 'flex-end';
          if (barIdx === groupMap[groupKey].length - 1) flex = 'flex-start';
          break;
        case 'spread':
          // Check first and last
          flex = 'center';
          if (barIdx === 0) flex = 'flex-start';
          if (barIdx === groupMap[groupKey].length - 1) flex = 'flex-end';
          break;
        case 'center':
          flex = 'center';
          break;
        default:
          flex = null;
          break;
      }

      // Save position in case custom bar
      imageModes[pointRow['~meta'].id] = flex;

      let labelX = isNegative ? realX - realWidth : realX;
      if (waterfall.enabled) {
        const idx = stackOrder.indexOf(groupKey);
        if (waterfallSums.includes(stackOrder[idx])) labelX = isNegative ? x + width : x - Math.abs(width - realWidth);
      }

      const renderConfig = dataLabel.getRenderConfig({
        mode: 'horizontal',
        animation,
        actualSize,
        htmlStr: dataLabelStr,
        value,
        availableWidth,
        availableHeight: 0,
        textRotate: chartManager.textRotate,
        color,
        backgroundColor,
        // x: isNegative ? realX - realWidth : realX,
        x: labelX,
        y,
        width: realWidth,
        height,
        labelWidth: dataLabelWidth,
        labelHeight: dataLabelHeight,
        chartWidth: chartManager.chartWidth,
        chartHeight: chartManager.chartHeight,
        zeroX: chartManager.xScale(0),
        zeroY: chartManager.yScale(0),
        dataLabelOffset: chartManager.dataLabelOffset,
        callbacks: callbacksQ,
        valuePosition: 'inside', // values.position,
        positionAltInside: 'stack', // values.positionAltInside
        positionAltOutside: values.positionAltOutside,
        flex,
      });

      if (renderConfig) overlayData.push(renderConfig);
    }
  }

  if (values.enabled && values.sumStacked) {
    const sumLabel = DataLabel.create({
      enabled: true,
      position: 'inside',
      lightnessThreshold: 0.5,
      format: values.format,
      style: values.sumStyle,
      darkColor: values.sumStyle.color,
      lightColor: values.sumStyle.color,
      forceDraw: values.forceDraw,
      quillValue: values.sumQuill,
      valueAlignment: values.valueAlignment,
    });

    const last = chartManager.dataStackPositive.length - 1;

    if (values.sumStackedTotal) {
      const sp = chartManager.dataStackPositive[last];
      const sn = chartManager.dataStackNegative[0];
      // Calculate totals and save them on the index [0]
      for (let i = 0; i < sp.length; i++) chartManager.dataStackPositive[last][i][0] = sn[i][1] + sp[i][1] > 0 ? sn[i][1] + sp[i][1] : 0;
      for (let i = 0; i < sn.length; i++) chartManager.dataStackNegative[0][i][0] = sn[i][1] + sp[i][1] < 0 ? sn[i][1] + sp[i][1] : 0;
      // Nullify values that should not be shown
      for (let i = 0; i < sp.length; i++) if (sn[i][1] + sp[i][1] < 0) chartManager.dataStackPositive[last][i][1] = 0;
      for (let i = 0; i < sn.length; i++) if (sn[i][1] + sp[i][1] > 0) chartManager.dataStackNegative[0][i][1] = 0;
    }

    const dataStackRange = [...chartManager.dataStackNegative[0], ...chartManager.dataStackPositive[last]];

    for (const stackRange of dataStackRange) {
      let stackSum = stackRange[1];
      if (stackSum === 0) continue;

      const sumRow = cloneObject(stackRange.data['~meta'].data[0]);
      sumRow[valueField] = values.sumStackedTotal ? stackRange[0] : stackSum;

      if (waterfall.enabled && sumRow[aggregationField]) sumRow[valueField] = waterfallAcc[sumRow[aggregationField] as string];

      const dynamicDataLabelStyle = cloneObject(values.sumStyle);
      dynamicDataLabelStyle.size = chartManager.getDynamicFontSize(values.sumStyle);

      const dataSumQuill = chartManager.getHTMLandRect(values.sumQuill, dynamicDataLabelStyle, sumRow, chartManager.content, true);

      const isNegative = stackRange[1] < 0;
      const aggregation = stackRange.data['~meta'].group;

      const x = chartManager.xScale(0);
      let realX = chartManager.xScale(stackRange[1]);
      const valueX = chartManager.xScale(stackRange[1]) + dataSumQuill.width * (isNegative ? -1 : 1);
      const { item } = chartManager.yScaleOrdinal(aggregation);
      const y = item.y;

      const width = Math.abs(valueX - x);
      const realWidth = Math.abs(valueX - realX);
      const height = item.height;

      const availableWidth = chartManager.horizontalSpace(stackSum);

      // callbacks
      const clickQ = (event, _, node) => {};
      const enterQ = (event, _, node) => {};
      const leaveQ = (event, _, node) => {};
      const moveQ = (event, _, node) => {};

      const callbacksQ = { click: clickQ, leave: leaveQ, enter: enterQ, move: moveQ };

      if (waterfall.enabled) {
        const pointGroup = sumRow[aggregationField] as string;
        const idx = stackOrder.indexOf(pointGroup);
        // Omit the first one
        if (idx > 0 && stackOrder[idx - 1]) {
          const offset = waterfallAcc[stackOrder[idx - 1]];
          realX = chartManager.xScale(stackRange[1] + offset);
        }

        if (waterfallSums.includes(stackOrder[idx])) {
          realX = isNegative ? x - width + realWidth : width - realWidth;
          stackSum += sumRow[valueField] as number;
        }
      }

      const renderConfig = sumLabel.getRenderConfig({
        ignoreColor: true,
        mode: 'horizontal',
        animation,
        actualSize: undefined,
        htmlStr: dataSumQuill.html,
        value: stackSum,
        availableWidth,
        availableHeight: 0,
        textRotate: chartManager.textRotate,
        color: backgroundColor,
        backgroundColor,
        x: isNegative ? realX - realWidth : realX,
        y,
        width: realWidth,
        height: height,
        labelWidth: dataSumQuill.width,
        labelHeight: dataSumQuill.height,
        chartWidth: chartManager.chartWidth,
        chartHeight: chartManager.chartHeight,
        zeroX: chartManager.xScale(0),
        zeroY: chartManager.yScale(0),
        dataLabelOffset: chartManager.dataLabelOffset,
        callbacks: callbacksQ,
        valuePosition: 'inside', // values.position,
        positionAltInside: 'stack', // values.positionAltInside
        positionAltOutside: values.positionAltOutside,
      });

      if (renderConfig) overlayData.push(renderConfig);
    }
  }

  const lineData: D3LineData[] = [];
  if (waterfall.enabled && waterfall.connectLine.enabled) {
    const { stroke, strokeWidth, strokeDasharray } = waterfall.connectLine;
    // Get x positions
    const xs = Object.entries(waterfallAcc)
      .map((acc) => acc[1])
      .slice(0, -1);
    // Get y positions
    const barY = [];
    for (const stack of [...chartManager.dataStackPositive, ...chartManager.dataStackNegative]) {
      for (const obj of stack) {
        const aggregation = obj.data['~meta'].group;
        const { item } = chartManager.yScaleOrdinal(aggregation);
        if (!barY.includes(item.y)) barY.push(item.y);
      }
    }
    const { item } = chartManager.yScaleOrdinal(aggregationField);
    const height = item.height;
    for (const [idx, x] of xs.entries()) {
      const y = barY[idx] + height;
      lineData.push({
        x1: chartManager.xScale(x),
        x2: chartManager.xScale(x),
        y1: y,
        y2: barY[idx + 1],
        color: stroke,
        width: strokeWidth,
        lineDash: strokeDasharrayFormat(strokeDasharray),
      });
    }
  }

  if (customBars) {
    for (const bar of customBars) {
      if (!bar.image.enabled) continue;

      const barAttrs = dataMap.find((row) => bar.id === row.label);
      const labelFlex = imageModes[barAttrs.label];

      const img = document.createElement('img');
      img.setAttribute('src', `${chartManager.globalService.escapeMediaLink(bar.image.url)}`);
      img.classList.add('object-contain', 'h-full');

      // img.style.borderRadius = `${bar.image.style.radius}%`;
      // const { strokeWidth, stroke, enabled } = bar.image.style.border;
      // if (enabled) img.style.border = `${strokeWidth}px ${stroke} solid`;

      const imgContainer = document.createElement('div');
      const imgFlexClass = anchorToFlexClass(bar.image.position, 'horizontal');
      imgContainer.classList.add('w-full', 'h-full', 'flex', 'items-center', 'overflow-hidden', imgFlexClass);
      // imgContainer.style.padding = `${bar.image.style.padding}px`;
      imgContainer.appendChild(img);

      // Adapt image to label in case positionMode flow
      if (bar.image.positionMode === 'absolute') {
        const { left, right } = bar.image.style.margin || { left: 0, right: 0 };
        imgContainer.style.marginLeft = `${left}px`;
        imgContainer.style.marginRight = `${right}px`;
      } else {
        const { totalWidth } = barAttrs.actualObj['~meta'];
        if (tailWindEqual[imgFlexClass] === labelFlex) {
          if (labelFlex === 'flex-start') imgContainer.style.marginLeft = `${totalWidth}px`;
          if (labelFlex === 'flex-end') imgContainer.style.marginRight = `${totalWidth}px`;
        }
      }

      const renderImgConfig = dataImage.getRenderConfig({
        mode: 'horizontal',
        animation,
        value: barAttrs.actualObj[valueField],
        x: roundTo(barAttrs.x),
        y: roundTo(barAttrs.y),
        width: roundTo(barAttrs.width),
        height: roundTo(barAttrs.height),
        imgHeight: 0,
        imgWidth: 0,
        imgRotate: false,
        position: bar.image.position,
        htmlStr: imgContainer.outerHTML,
        callbacks: {},
      });

      if (bar.image.url && renderImgConfig) imageData.push(renderImgConfig);
    }
  }

  // clear
  d3renderPath(chartManager.chart.element, [], '.chart');
  d3renderCircle(chartManager.chart.element, [], '.chart');
  d3renderGradient(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.chart');
  d3renderPathFilled(chartManager.chart.element, [], '.voronoi');
  d3renderText(chartManager.chart.element, [], '.text');

  d3renderRect(chartManager.chart.element, data, '.chart', chartManager.animationStop);
  // d3renderText(chartManager.chart, textData, '.text');

  d3renderLine(chartManager.chart.element, waterfall.enabled && waterfall.connectLine.enabled ? lineData : [], '.chart');
  overlayImage(chartManager.overlay, customBars ? imageData : [], '.overlay-images');

  overlayText(chartManager.overlay, overlayData, '.overlay-values');
};
// #endregion
